// The ordered top-level SECTIONS the current viewer can reach — the swipe roster for the app's root scroll-snap
// surface. Mirrors app-section-nav.tsx in the SAME order, so the nav, what you can swipe between, and which way a
// section link travels are one fact. Members are section ROOT pathnames; a URL deeper inside resolves to its root
// via the engine's prefix match.
//
// It is also the bottom bar's gate: `length <= 1` means nothing to navigate between, so the bar doesn't render
// (app-bottom-nav.tsx) and the pager renders an inert single slot.

import { useSessionUser } from '@/hooks/use-session'
import { usePermissions } from '@/hooks/use-permissions'
import { useActiveEmployer } from '@/hooks/use-active-employer'

export function useTopLevelSections(): string[] {
  const user = useSessionUser()
  const { isCompanyView, can } = usePermissions()
  const { activeEmployerId } = useActiveEmployer()

  if (!user) return []

  const isApplicant = user.type === 'applicant'
  const isSelfApplicant = isApplicant && !user.isEmployee
  const isEmployee = isApplicant && user.isEmployee
  const canAccessOrgs = can('orgs:read')

  // No manual memo: the React Compiler auto-memoizes this derivation, so the array keeps its identity while
  // the gates below are unchanged. That identity matters — the pager takes it as `group` and keys callbacks
  // and effects on it (see route-transition/surface/scroll-transition-outlet).
  const sections: string[] = []
  if (canAccessOrgs) sections.push('/orgs')
  if (isCompanyView) sections.push('/employees')
  if (isApplicant && !isCompanyView) sections.push('/report')
  // Position (employee, own view) and Jobs (company view) are the same /jobs root, shown in mutually
  // exclusive states — so it appears at most once.
  if ((isEmployee && activeEmployerId !== null && !isCompanyView) || isCompanyView) sections.push('/jobs')
  if (!isSelfApplicant && (!isEmployee || activeEmployerId !== null)) sections.push('/teams')
  if (!isSelfApplicant && (!isEmployee || isCompanyView)) sections.push('/offers')
  if (isCompanyView) sections.push('/market')

  return sections
}
